import * as React from 'react';
import { Query } from 'react-apollo';

import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import ErrorReporter from 'app/components/ErrorReporter';
import GET_FULL_COACH_SCHEDULE_QUERY from 'coaching/queries/GetFullCoachSchedule.gql';
import {
  GetFullCoachSchedule,
  GetFullCoachSchedule_coaching_coachAvailability_availabilities,
  GetFullCoachSchedule_coaching_coachAvailability_availabilityExceptions,
  GetFullCoachSchedule_coaching_getUpcomingSessions,
} from 'coaching/queries/__generated__/GetFullCoachSchedule';

export type CoachAvailability = GetFullCoachSchedule_coaching_coachAvailability_availabilities;
export type CoachAvailabilityException = GetFullCoachSchedule_coaching_coachAvailability_availabilityExceptions;
export type CoachUpcomingSession = GetFullCoachSchedule_coaching_getUpcomingSessions;

export type Props = {
  coachUuid?: string;
  startDateTime: string;
  endDateTime: string;
  timezone: string;
  children: (props: {
    coachAvailabilities: CoachAvailability[];
    coachAvailabilityExceptions: CoachAvailabilityException[];
    upcomingSessions: CoachUpcomingSession[];
  }) => React.ReactNode;
};

export default function GetFullCoachScheduleContainer(props: Props) {
  const errorMessage = 'Unable to retrieve the coach’s schedule - please refresh and try again.';
  if (!props.coachUuid) {
    return (
      <ErrorReporter
        error={`GetFullCoachScheduleContainer - tried to fetch coach schedule without coachUuid`}>
        {errorMessage}
      </ErrorReporter>
    );
  }

  return (
    <Query<GetFullCoachSchedule>
      query={GET_FULL_COACH_SCHEDULE_QUERY}
      variables={{
        coachUuid: props.coachUuid,
        startDateTime: props.startDateTime,
        endDateTime: props.endDateTime,
        timezone: props.timezone,
      }}>
      {({ loading, error, data }) => {
        if (loading) {
          return <CenteredLoadingIndicator />;
        }

        if (error || !data?.coaching) {
          return (
            <ErrorReporter
              error={`GetFullCoachScheduleContainer - unable to fetch data error=${error?.message}`}>
              {errorMessage}
            </ErrorReporter>
          );
        }

        const { coachAvailability, getUpcomingSessions } = data.coaching;

        return props.children({
          coachAvailabilities: coachAvailability.availabilities.filter(
            (availability): availability is CoachAvailability => !!availability
          ),
          coachAvailabilityExceptions: coachAvailability.availabilityExceptions.filter(
            (exception): exception is CoachAvailabilityException => !!exception
          ),
          upcomingSessions: getUpcomingSessions,
        });
      }}
    </Query>
  );
}
